import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Modal as ModalUI, Button, Alert } from "flowbite-react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import axios from "../api/axios";
import { KeyIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon } from "@heroicons/react/24/outline";

export default function GenerateAPIKeyModal({ handleUpdates }) {
  const [showModal, setShowModal] = useState(false);
  const [showError, setShowError] = useState(false);
  const [key, setKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [hasGeneratedKey, setHasGeneratedKey] = useState(false);
  const { getToken, logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkExistingKey = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`/profile/haskey`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.data?.hasApiKey) {
          setHasGeneratedKey(true);
        }
      } catch (error) {
        console.error("Error checking API key status:", error);
      }
    };

    checkExistingKey();
  }, []);

  const generateAPIKey = async () => {
    setShowError(false);
    setCopied(false);
    setIsLoading(true);

    try {
      const token = getToken();
      const response = await axios.post(
        `/profile/key`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.apiKey) {
        setKey(response.data.apiKey);
        setHasGeneratedKey(true);
      } else {
        throw new Error("Invalid API Key response");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.error("Unauthorized: Invalid Token", error);
        logout();
      } else {
        setShowError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `/profile/key/regenerate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data?.newApiKey) {
        setKey(response.data.newApiKey);
        setHasGeneratedKey(true);
        setShowError(false);
      } else {
        setShowError(true);
      }
    } catch (error) {
      console.error("Error regenerating API key:", error);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    handleUpdates();
    setShowModal(false);
    setCopied(false);
    setConfirmText("");
  };

  return (
    <div>
      {!hasGeneratedKey ? (
        <Button
          size="sm"
          color="success"
          className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 whitespace-nowrap"
          onClick={() => {
            setShowModal(true);
            generateAPIKey();
          }}
        >
          <KeyIcon className="h-5 w-5" />
          <span className="ml-2">Generate API Key</span>
        </Button>
      ) : (
        <Button
          size="sm"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm hover:shadow-md transition-all duration-300 whitespace-nowrap"
          onClick={() => {
            setShowModal(true);
            setKey("");
          }}
        >
          <ArrowPathIcon className="h-5 w-5" />
          <span className="ml-2">Regenerate API Key</span>
        </Button>
      )}

      <ModalUI
        show={showModal}
        size="3xl"
        onClose={closeModal}
        className="rounded-xl overflow-hidden shadow-2xl"
      >
        <ModalUI.Header className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center gap-3">
            <KeyIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
              {hasGeneratedKey && !key ? "Regenerate API Key" : "API Key Management"}
            </h3>
          </div>
        </ModalUI.Header>
        
        <ModalUI.Body className="bg-white dark:bg-gray-900 p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">Processing your request...</p>
            </div>
          ) : hasGeneratedKey && !key ? (
            <div className="mb-4 flex flex-col gap-4">
              <div className="flex p-4 text-amber-800 bg-amber-100 dark:bg-amber-200 border-l-4 border-amber-500 rounded-lg shadow-sm">
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                <div>
                  <div className="font-medium text-lg mb-1">Warning!</div>
                  <p>
                    Your previous API key will no longer be valid.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-4 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <label htmlFor="confirm-regenerate" className="text-gray-700 dark:text-gray-300 font-medium">
                  Type <span className="font-bold text-indigo-600 dark:text-indigo-400">Regenerate API Key</span> to confirm:
                </label>
                <input
                  id="confirm-regenerate"
                  type="text"
                  className="border border-gray-300 dark:border-gray-600 px-4 py-3 rounded-lg w-full text-gray-800 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300"
                  placeholder="Type here to confirm..."
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <Button
                color="success"
                disabled={confirmText !== "Regenerate API Key"}
                className={`mt-4 py-2.5 px-5 rounded-lg shadow-sm ${
                  confirmText === "Regenerate API Key" 
                    ? "bg-indigo-600 hover:bg-indigo-700 hover:shadow-md" 
                    : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                } transition-all duration-300`}
                onClick={handleRegenerate}
              >
                <div className="flex items-center justify-center gap-2">
                  <KeyIcon className="h-5 w-5" />
                  <span>Regenerate API Key</span>
                </div>
              </Button>
            </div>
          ) : key ? (
            <div className="w-full flex flex-col gap-6 items-center py-4">
              <div className="flex p-4 text-red-800 bg-red-100 dark:bg-red-200 border-l-4 border-red-500 rounded-lg shadow-sm w-full">
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                <div>
                  <div className="font-medium text-lg mb-1">Important!</div>
                  <p>
                    This key will not be shown again. Copy and store it securely.
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 w-full shadow-sm">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Your API Key:</div>
                <div className="flex gap-3 w-full items-center">
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg w-full bg-white dark:bg-gray-700 px-5 py-4 font-mono text-sm lg:text-base overflow-x-auto">
                    <p className="text-gray-800 dark:text-gray-100 break-all select-all">{key}</p>
                  </div>
                  <CopyToClipboard text={key} onCopy={() => setCopied(true)}>
                    <Button 
                      color={copied ? "success" : "light"}
                      className={`min-w-24 h-12 rounded-lg shadow-sm ${
                        copied 
                          ? "bg-green-500 hover:bg-green-600 text-white" 
                          : "bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                      } transition-all duration-300`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {copied ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                        <span>{copied ? "Copied!" : "Copy"}</span>
                      </div>
                    </Button>
                  </CopyToClipboard>
                </div>
              </div>

              <div className="text-center text-gray-600 dark:text-gray-300 text-sm mt-2">
                Remember to keep this key secure and never share it publicly
              </div>
            </div>
          ) : (
            !hasGeneratedKey && showError && (
              <div className="flex p-4 text-red-800 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm mt-12">
                <ExclamationTriangleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
                <div>
                  <div className="font-medium text-lg mb-1">Error!</div>
                  <p>
                    Unable to generate API key. Please try again later.
                  </p>
                </div>
              </div>
            )
          )}
        </ModalUI.Body>
      </ModalUI>
    </div>
  );
}